import React, { useEffect } from "react";
import { OutlineStyle, OutlineStyleProps } from "./OutlineStyle";
import { OutlineCategory, OutlineCategoryProps } from "./OutlineCategory";
import { OutlineStylesContainer } from "./OutlineStylesContainer";
import "./OutlineGroup.css";

import type { TOutlineGroup } from "./types";

export interface OutlineGroupProps
  extends
    Pick<OutlineCategoryProps, "onClearGroup" | "onSelectGroup" | "onSetBoundaryIndices">,
    Pick<
      OutlineStyleProps,
      "ignoreParagraphs" | "outlineMode" | "onClearStyle" | "onSelectStyle" | "styleDefinitionMap"
    > {
  expandAll: boolean;
  group: TOutlineGroup;
  selectedGroup: (group: TOutlineGroup) => boolean;
  selectedStyle: OutlineStyleProps["selected"];
}

export function OutlineGroup({
  expandAll,
  group,
  ignoreParagraphs,
  outlineMode,
  styleDefinitionMap,
  onClearGroup,
  onSelectGroup,
  onClearStyle,
  onSelectStyle,
  onSetBoundaryIndices,
  selectedGroup,
  selectedStyle,
}: OutlineGroupProps) {
  const [isExpanded, setIsExpanded] = React.useState(selectedGroup(group));

  useEffect(() => {
    if (selectedGroup(group)) {
      setIsExpanded(true);
    } else {
      setIsExpanded(expandAll);
    }
  }, [expandAll]);

  const notInProfile = group.styleGroup.some((sg) => {
    const styleDef = styleDefinitionMap?.[sg.name];
    return !styleDef;
  });

  const handleExpand = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="outline-group">
      <OutlineCategory
        {...group}
        expanded={isExpanded}
        notInProfile={notInProfile}
        selected={selectedGroup(group)}
        onExpand={handleExpand}
        onClearGroup={onClearGroup}
        onSelectGroup={onSelectGroup}
        onSetBoundaryIndices={onSetBoundaryIndices}
      />
      <OutlineStylesContainer expanded={isExpanded}>
        {group.styleGroup.map((sg) => (
          <OutlineStyle
            key={`${group.id}-${sg.name}`}
            {...sg}
            group={group}
            ignoreParagraphs={ignoreParagraphs}
            outlineMode={outlineMode}
            onClearStyle={onClearStyle}
            onSelectStyle={onSelectStyle}
            onSetBoundaryIndices={onSetBoundaryIndices}
            selected={(style) => {
              return selectedStyle(style) && selectedGroup(group);
            }}
            styleDefinitionMap={styleDefinitionMap}
          />
        ))}
      </OutlineStylesContainer>
    </div>
  );
}
